/**
 * GTFS feed configuration preset. core stores `configuration` as an opaque
 * string map; the GTFS provider parses it (`internal/provider/config.go` in the
 * GTFS repo), so the keys, defaults and value formats here mirror that parser.
 *
 * GTFS only checks *types* (a bad integer/boolean/line-name source makes the
 * feed REJECTED, with the key named in statusMessage) — it doesn't check
 * ranges or URL formats, so those are enforced here.
 *
 * A missing or empty key means "use the default" (for URLs: "not
 * configured"), and every update replaces the whole map, so empty fields are
 * simply left out of the saved configuration.
 */

/** Provider `type` that gets this preset (GTFS's PROVIDER_TYPE default). */
export const GTFS_PROVIDER_TYPE = 'gtfs';

export const GTFS_LINE_NAME_SOURCES = ['route_long_name', 'destination_stop_name', 'trip_headsign'] as const;
export type GtfsLineNameSource = (typeof GTFS_LINE_NAME_SOURCES)[number];

export type GtfsUrlField = 'tripUpdatesUrl' | 'alertsUrl' | 'vehiclePositionsUrl';
export type GtfsSecondsField =
  | 'pollIntervalSecs'
  | 'boardLookbackSecs'
  | 'boardWindowSecs'
  | 'significantChangeThresholdSecs'
  | 'heartbeatIntervalSecs';

export interface GtfsConfigForm {
  tripUpdatesUrl: string;
  alertsUrl: string;
  vehiclePositionsUrl: string;
  /** null = blank = the GTFS default. */
  pollIntervalSecs: number | null;
  boardLookbackSecs: number | null;
  boardWindowSecs: number | null;
  significantChangeThresholdSecs: number | null;
  heartbeatIntervalSecs: number | null;
  cleardownOnVehiclePosition: boolean;
  /** null while the stored value isn't a source GTFS supports — see
   *  `unrecognisedLineNameSource`. */
  lineNameSource: GtfsLineNameSource | null;
  /** A stored `marketing_line_name_source` GTFS doesn't support (it REJECTS the
   *  feed for it), kept so the form can show it; cleared once one is picked. */
  unrecognisedLineNameSource: string | null;
  /** Keys this form doesn't know about, carried through a save untouched
   *  (GTFS ignores unknown keys, but a newer GTFS might not). */
  extra: Record<string, string>;
}

const URL_KEYS: Record<GtfsUrlField, string> = {
  tripUpdatesUrl: 'rt_trip_updates_url',
  alertsUrl: 'rt_alerts_url',
  vehiclePositionsUrl: 'rt_vehicle_positions_url',
};

const SECONDS_KEYS: Record<GtfsSecondsField, string> = {
  pollIntervalSecs: 'poll_interval_secs',
  boardLookbackSecs: 'board_lookback_secs',
  boardWindowSecs: 'board_window_secs',
  significantChangeThresholdSecs: 'significant_change_threshold_secs',
  heartbeatIntervalSecs: 'heartbeat_interval_secs',
};

const CLEARDOWN_KEY = 'cleardown_on_vehicle_position';
const LINE_NAME_KEY = 'marketing_line_name_source';
const KNOWN_KEYS = new Set([...Object.values(URL_KEYS), ...Object.values(SECONDS_KEYS), CLEARDOWN_KEY, LINE_NAME_KEY]);

/** GTFS's own defaults (store.DefaultFeedSettings), shown as placeholders. */
export const GTFS_DEFAULTS: Record<GtfsSecondsField, number> & { lineNameSource: GtfsLineNameSource } = {
  pollIntervalSecs: 30,
  boardLookbackSecs: 5400,
  boardWindowSecs: 7200,
  significantChangeThresholdSecs: 30,
  heartbeatIntervalSecs: 300,
  lineNameSource: 'route_long_name',
};

/** Client-side floors. Lookback may be 0 (drop departures as soon as they're
 *  due); everything else must be positive. */
export const GTFS_MINIMUMS: Record<GtfsSecondsField, number> = {
  pollIntervalSecs: 1,
  boardLookbackSecs: 0,
  boardWindowSecs: 1,
  significantChangeThresholdSecs: 1,
  heartbeatIntervalSecs: 1,
};

export type GtfsFieldError = 'url' | 'integer' | 'min' | 'unrecognised';
export type GtfsFormField = GtfsUrlField | GtfsSecondsField | 'lineNameSource';

export function emptyGtfsForm(): GtfsConfigForm {
  return {
    tripUpdatesUrl: '',
    alertsUrl: '',
    vehiclePositionsUrl: '',
    pollIntervalSecs: null,
    boardLookbackSecs: null,
    boardWindowSecs: null,
    significantChangeThresholdSecs: null,
    heartbeatIntervalSecs: null,
    cleardownOnVehiclePosition: false,
    lineNameSource: GTFS_DEFAULTS.lineNameSource,
    unrecognisedLineNameSource: null,
    extra: {},
  };
}

/** Realtime is on only with a trip-updates URL; the other two URLs and the
 *  cleardown toggle are inert without it. */
export function gtfsRealtimeEnabled(form: GtfsConfigForm): boolean {
  return !!form.tripUpdatesUrl.trim();
}

/** Cleardown acts on vehicle positions, so it also needs that URL. */
export function gtfsCleardownEnabled(form: GtfsConfigForm): boolean {
  return gtfsRealtimeEnabled(form) && !!form.vehiclePositionsUrl.trim();
}

/** An existing feed's configuration → form. An unparseable stored integer
 *  becomes blank (the default); the feed would be REJECTED for it anyway. */
export function gtfsFormFromConfiguration(config: Record<string, string> | null): GtfsConfigForm {
  const c = config ?? {};
  const form = emptyGtfsForm();
  for (const [field, key] of Object.entries(URL_KEYS) as [GtfsUrlField, string][]) {
    form[field] = c[key] ?? '';
  }
  for (const [field, key] of Object.entries(SECONDS_KEYS) as [GtfsSecondsField, string][]) {
    const raw = (c[key] ?? '').trim();
    form[field] = /^-?\d+$/.test(raw) ? Number(raw) : null;
  }
  form.cleardownOnVehiclePosition = (c[CLEARDOWN_KEY] ?? '').trim().toLowerCase() === 'true';
  const source = (c[LINE_NAME_KEY] ?? '').trim();
  if ((GTFS_LINE_NAME_SOURCES as readonly string[]).includes(source)) {
    form.lineNameSource = source as GtfsLineNameSource;
  } else if (source) {
    // Empty = GTFS default; anything else unknown is kept so it can be shown and fixed.
    form.lineNameSource = null;
    form.unrecognisedLineNameSource = source;
  }
  form.extra = Object.fromEntries(Object.entries(c).filter(([key]) => !KNOWN_KEYS.has(key)));
  return form;
}

/** Form → configuration map. Blank fields are omitted (= GTFS default), as
 *  are the realtime-only settings while realtime is off. */
export function gtfsConfigurationFromForm(form: GtfsConfigForm): Record<string, string> {
  const config: Record<string, string> = { ...form.extra };
  const realtime = gtfsRealtimeEnabled(form);
  if (realtime) {
    for (const [field, key] of Object.entries(URL_KEYS) as [GtfsUrlField, string][]) {
      const url = form[field].trim();
      if (url) config[key] = url;
    }
    if (gtfsCleardownEnabled(form) && form.cleardownOnVehiclePosition) config[CLEARDOWN_KEY] = 'true';
  }
  for (const [field, key] of Object.entries(SECONDS_KEYS) as [GtfsSecondsField, string][]) {
    const value = form[field];
    if (value != null) config[key] = String(value);
  }
  // Never null here in practice: an unrecognised source is a form error that blocks Save.
  config[LINE_NAME_KEY] = form.lineNameSource ?? form.unrecognisedLineNameSource ?? GTFS_DEFAULTS.lineNameSource;
  return config;
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export type GtfsFormErrors = Partial<Record<GtfsFormField, GtfsFieldError>>;

/** Per-field problems; empty object = saveable. Disabled (realtime-off)
 *  URLs aren't validated since they won't be saved. An unrecognised stored
 *  line-name source is an error until a supported one is picked. */
export function gtfsFormErrors(form: GtfsConfigForm): GtfsFormErrors {
  const errors: GtfsFormErrors = {};
  const realtime = gtfsRealtimeEnabled(form);
  for (const field of Object.keys(URL_KEYS) as GtfsUrlField[]) {
    if (field !== 'tripUpdatesUrl' && !realtime) continue;
    const url = form[field].trim();
    if (url && !isHttpUrl(url)) errors[field] = 'url';
  }
  for (const field of Object.keys(SECONDS_KEYS) as GtfsSecondsField[]) {
    const value = form[field];
    if (value == null) continue;
    if (!Number.isInteger(value)) errors[field] = 'integer';
    else if (value < GTFS_MINIMUMS[field]) errors[field] = 'min';
  }
  if (!form.lineNameSource) errors.lineNameSource = 'unrecognised';
  return errors;
}
